import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { Plus, Trash2, Search, ChevronDown, X, Info } from 'lucide-react';
import { useSearchQBItems, useSearchQBAccounts } from '@api/hooks/useQBItems';
import { useErpFieldOptions } from '@api/hooks/useErpFields';
import { ExpressionNumberInput } from '@components/common/ExpressionNumberInput';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@components/common/Dialog';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import type { ErpFieldValue, QBItemResponse, QBAccountResponse, TenantErpFieldConfig } from '@schemas/api';
import { isBillDetailAccountAllowed } from '@utils/erpAccountEligibility';

interface LineItem {
  [key: string]: any;
}

type PostingLineType = 'item' | 'category';
type BulkLineFieldKey = 'item' | 'category' | string;

interface DynamicErpBulkValueTemplate {
  value: any;
  display_value: string;
  reference_option_id: string | null;
}

const QUANTITY_FIELDS = ['quantity', 'qty'] as const;
const UNIT_PRICE_FIELDS = ['unit_price', 'price'] as const;

function readNumericField(item: LineItem, fields: readonly string[]): number | null {
  for (const field of fields) {
    const rawValue = item[field];
    if (rawValue === '' || rawValue === null || rawValue === undefined) {
      continue;
    }

    const numericValue = typeof rawValue === 'number' ? rawValue : parseFloat(String(rawValue));
    if (!Number.isNaN(numericValue)) {
      return numericValue;
    }
  }

  return null;
}

function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function withCalculatedLineTotal(item: LineItem, changedField: string): LineItem {
  if (!QUANTITY_FIELDS.includes(changedField as typeof QUANTITY_FIELDS[number]) &&
      !UNIT_PRICE_FIELDS.includes(changedField as typeof UNIT_PRICE_FIELDS[number])) {
    return item;
  }

  const quantity = readNumericField(item, QUANTITY_FIELDS);
  const unitPrice = readNumericField(item, UNIT_PRICE_FIELDS);

  if (quantity === null || unitPrice === null) {
    return { ...item, total: '' };
  }

  return { ...item, total: roundCurrency(quantity * unitPrice) };
}

interface LineItemsEditorProps {
  items: LineItem[];
  onChange: (items: LineItem[]) => void;
  documentType?: 'purchase_order' | 'receipt' | 'invoice';
  selectedLineItemFields?: string[];
  dynamicErpFieldConfigs?: TenantErpFieldConfig[];
  dynamicErpValues?: ErpFieldValue[];
  onDynamicErpValuesChange?: (values: ErpFieldValue[]) => void;
  fillAvailableHeight?: boolean;
  className?: string;
  erpConnectionId?: string;
  billDetailProvider?: string;
}

/** Compact inline dropdown for selecting a QB item on a line. */
export const QBItemCell: React.FC<{
  value: string | null;
  onSelect: (itemId: string, itemName: string) => void;
  onClear: () => void;
  disabled?: boolean;
  erpConnectionId?: string;
}> = ({ value, onSelect, onClear, disabled = false, erpConnectionId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useSearchQBItems(debouncedSearch, 0, 50, erpConnectionId);
  const results = data?.items || [];

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Find the selected item name from results (best-effort display)
  const selectedName = value
    ? results.find((r) => r.id === value || r.qb_item_id === value)?.name
    : null;

  return (
    <div ref={containerRef} className="relative min-w-[180px]">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setIsOpen(!isOpen);
            }
          }}
          disabled={disabled}
          className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm flex items-center justify-between gap-1 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="truncate text-left text-gray-900 dark:text-gray-100">
            {selectedName || value || 'Select item...'}
          </span>
          <ChevronDown className={`h-3 w-3 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Clear"
          >
            <X className="h-3 w-3 text-gray-400" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search items..."
                className="w-full pl-7 pr-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {isLoading && (
              <div className="p-3 text-center text-xs text-gray-500 dark:text-gray-400">Loading...</div>
            )}

            {!isLoading && results.length === 0 && (
              <div className="p-3 text-center text-xs text-gray-500 dark:text-gray-400">
                {search ? `No results for "${search}"` : 'Type to search items'}
              </div>
            )}

            {results.map((item: QBItemResponse) => {
              const isSelected = value === item.id || value === item.qb_item_id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => {
                    onSelect(item.id, item.name);
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                    isSelected ? 'bg-blue-100 dark:bg-blue-800' : ''
                  }`}
                >
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {item.sku ? `SKU: ${item.sku}` : item.item_type}
                    {item.description ? ` — ${item.description}` : ''}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/** Compact inline dropdown for selecting a synced ERP account. */
export const QBAccountCell: React.FC<{
  value: string | null;
  displayValue?: string | null;
  onSelect: (accountId: string, accountName: string) => void;
  onClear: () => void;
  accountType?: string;
  disabled?: boolean;
  erpConnectionId?: string;
  billDetailProvider?: string;
}> = ({
  value,
  displayValue,
  onSelect,
  onClear,
  accountType,
  disabled = false,
  erpConnectionId,
  billDetailProvider,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useSearchQBAccounts(debouncedSearch, accountType, 0, 50, erpConnectionId);
  const results = (data?.accounts || []).filter((account) =>
    !billDetailProvider || isBillDetailAccountAllowed(billDetailProvider, account.account_type)
  );
  const formatAccountLabel = (account: QBAccountResponse) =>
    account.account_number ? `#${account.account_number} ${account.name}` : account.name;

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const selectedName = value
    ? (() => {
        const selected = results.find((r) => r.id === value || r.qb_account_id === value);
        return selected ? formatAccountLabel(selected) : null;
      })()
    : null;

  return (
    <div ref={containerRef} className="relative min-w-[180px]">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              setIsOpen(!isOpen);
            }
          }}
          disabled={disabled}
          className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-sm flex items-center justify-between gap-1 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <span className="truncate text-left text-gray-900 dark:text-gray-100">
            {selectedName || displayValue || value || 'Select account...'}
          </span>
          <ChevronDown className={`h-3 w-3 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            title="Clear"
          >
            <X className="h-3 w-3 text-gray-400" />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-72 z-50 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or account number..."
                className="w-full pl-7 pr-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-48 overflow-y-auto">
            {isLoading && (
              <div className="p-3 text-center text-xs text-gray-500 dark:text-gray-400">Loading...</div>
            )}

            {!isLoading && results.length === 0 && (
              <div className="p-3 text-center text-xs text-gray-500 dark:text-gray-400">
                {search ? `No results for "${search}"` : 'Type to search accounts'}
              </div>
            )}

            {results.map((account: QBAccountResponse) => {
              const isSelected = value === account.id || value === account.qb_account_id;
              return (
                <button
                  key={account.id}
                  type="button"
                  onClick={() => {
                    onSelect(account.id, formatAccountLabel(account));
                    setIsOpen(false);
                    setSearch('');
                  }}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900 border-b border-gray-100 dark:border-gray-700 last:border-b-0 ${
                    isSelected ? 'bg-blue-100 dark:bg-blue-800' : ''
                  }`}
                >
                  <p className="font-medium text-gray-900 dark:text-gray-100 truncate">{account.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {account.account_number ? `#${account.account_number} — ` : ''}{account.account_type}
                    {account.account_sub_type ? ` / ${account.account_sub_type}` : ''}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const LINE_ITEM_FIELDS = ['description', 'quantity', 'unit_price', 'total'];

const SHOW_QB_ITEM_CELL: Record<string, boolean> = {
  invoice: true,
  purchase_order: false,
  receipt: false,
};

const lineValueKey = (fieldKey: string, lineId?: string | null, lineNumber?: number) =>
  `${fieldKey}:${lineId || `line-${lineNumber ?? ''}`}`;

const fieldKeyTail = (fieldKey?: string | null): string =>
  fieldKey && fieldKey.includes('.') ? fieldKey.split('.').slice(1).join('.') : fieldKey || '';

const normalizedLineNumber = (value?: unknown) =>
  Number.isFinite(Number(value)) ? Number(value) : undefined;

const evidenceLineNumber = (value: ErpFieldValue) =>
  normalizedLineNumber(value.evidence?.line_number);

const lineValueKeys = (fieldKey: string, lineId?: string | null, lineNumber?: number | null) => {
  const normalizedNumber = normalizedLineNumber(lineNumber);
  const keys = [lineValueKey(fieldKey, lineId, normalizedNumber)];
  if (normalizedNumber !== undefined) {
    keys.push(lineValueKey(fieldKey, null, normalizedNumber));
  }
  return keys;
};

const erpValueLineKeys = (value: ErpFieldValue) =>
  lineValueKeys(value.field_key, value.line_id, evidenceLineNumber(value));

const addDynamicValueLookup = (lookup: Map<string, ErpFieldValue>, value: ErpFieldValue) => {
  erpValueLineKeys(value).forEach((key) => lookup.set(key, value));
};

const getDynamicLineValue = (
  lookup: Map<string, ErpFieldValue>,
  fieldKey: string,
  lineId?: string | null,
  lineNumber?: number | null,
) => {
  for (const key of lineValueKeys(fieldKey, lineId, lineNumber)) {
    const value = lookup.get(key);
    if (value) {
      return value;
    }
  }
  return undefined;
};

const rowIdentity = (item: LineItem, index: number) =>
  String(item.id || item.ap_line_id || `line-${item.line_number ?? index + 1}`);

export const DynamicErpLineFieldCell: React.FC<{
  config: TenantErpFieldConfig;
  item: LineItem;
  value?: ErpFieldValue;
  onChange: (value: ErpFieldValue) => void;
  disabled?: boolean;
  lineIdOverride?: string | null;
}> = ({ config, item, value, onChange, disabled = false, lineIdOverride }) => {
  const isReference = config.field.value_type === 'reference' || config.field.value_type === 'multi_reference';
  const optionsQuery = useErpFieldOptions(
    {
      field_definition_id: config.field_definition_id,
      object_type: config.field.object_type || undefined,
      erp_connection_id: config.field.erp_connection_id || undefined,
      limit: 100,
    },
    isReference,
  );
  const options = optionsQuery.data?.items || [];
  const lineId = lineIdOverride !== undefined ? lineIdOverride : item.id || item.ap_line_id || null;

  if (isReference) {
    return (
      <SearchableSelect
        value={value?.reference_option_id || ''}
        disabled={disabled}
        onChange={(selectedValue) => {
          const option = options.find((entry) => entry.id === selectedValue);
          onChange({
            field_definition_id: config.field_definition_id,
            field_key: config.field.field_key,
            line_id: lineId,
            value: option?.external_id || '',
            display_value: option?.label || '',
            reference_option_id: option?.id || null,
            source: 'manual',
            evidence: { line_number: item.line_number },
          });
        }}
        options={[{ id: '', label: 'Select...' }, ...options.map((option) => ({ id: option.id, label: option.label }))]}
        placeholder="Select..."
        clearable={false}
      />
    );
  }

  return (
    <input
      type={config.field.value_type === 'number' ? 'number' : config.field.value_type === 'date' ? 'date' : 'text'}
      value={String(value?.display_value ?? value?.value ?? '')}
      disabled={disabled}
      onChange={(event) => onChange({
        field_definition_id: config.field_definition_id,
        field_key: config.field.field_key,
        line_id: lineId,
        value: event.target.value,
        display_value: event.target.value,
        source: 'manual',
        evidence: { line_number: item.line_number },
      })}
      className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
    />
  );
};

export const DynamicErpBulkFieldPicker: React.FC<{
  config: TenantErpFieldConfig;
  disabled?: boolean;
  onApply: (value: DynamicErpBulkValueTemplate) => void;
}> = ({ config, disabled = false, onApply }) => {
  const [pendingValue, setPendingValue] = useState('');
  const isReference = config.field.value_type === 'reference' || config.field.value_type === 'multi_reference';
  const optionsQuery = useErpFieldOptions(
    {
      field_definition_id: config.field_definition_id,
      object_type: config.field.object_type || undefined,
      erp_connection_id: config.field.erp_connection_id || undefined,
      limit: 100,
    },
    isReference,
  );
  const options = optionsQuery.data?.items || [];

  useEffect(() => {
    setPendingValue('');
  }, [config.id]);

  if (isReference) {
    return (
      <SearchableSelect
        value={pendingValue}
        disabled={disabled}
        onChange={(selectedValue) => {
          setPendingValue(selectedValue);
          const option = options.find((entry) => entry.id === selectedValue);
          if (!option) {
            return;
          }
          onApply({
            value: option.external_id || '',
            display_value: option.label || '',
            reference_option_id: option.id || null,
          });
          setPendingValue('');
        }}
        options={[
          { id: '', label: `Choose ${config.field.label.toLowerCase()}...` },
          ...options.map((option) => ({ id: option.id, label: option.label })),
        ]}
        placeholder={`Choose ${config.field.label.toLowerCase()}...`}
        clearable={false}
      />
    );
  }

  return (
    <div className="flex min-w-[220px] items-center gap-2">
      <input
        type={config.field.value_type === 'number' ? 'number' : config.field.value_type === 'date' ? 'date' : 'text'}
        value={pendingValue}
        disabled={disabled}
        onChange={(event) => setPendingValue(event.target.value)}
        className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      />
      <button
        type="button"
        disabled={disabled || pendingValue === ''}
        onClick={() => {
          onApply({
            value: pendingValue,
            display_value: pendingValue,
            reference_option_id: null,
          });
          setPendingValue('');
        }}
        className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:border-gray-400 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:border-gray-500 dark:hover:text-gray-100"
      >
        Apply
      </button>
    </div>
  );
};

export const LineItemsEditor: React.FC<LineItemsEditorProps> = ({
  items,
  onChange,
  documentType = 'invoice',
  selectedLineItemFields,
  dynamicErpFieldConfigs = [],
  dynamicErpValues = [],
  onDynamicErpValuesChange,
  fillAvailableHeight = false,
  className = '',
  erpConnectionId,
  billDetailProvider,
}) => {
  const [expandedDescriptionIndex, setExpandedDescriptionIndex] = useState<number | null>(null);
  const [selectedRowIndexes, setSelectedRowIndexes] = useState<Set<number>>(new Set());
  const [linePostingTypes, setLinePostingTypes] = useState<Map<string, PostingLineType>>(new Map());
  const [bulkLineFieldKey, setBulkLineFieldKey] = useState<BulkLineFieldKey>('item');

  const handleAddItem = useCallback(() => {
    const newItem: LineItem = {
      line_number: items.length + 1,
      description: '',
      quantity: 0,
      unit_price: 0,
      total: 0,
    };
    onChange([...items, newItem]);
  }, [items, onChange]);

  const handleDeleteItem = useCallback((index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems.map((item, idx) => ({ ...item, line_number: idx + 1 })));
  }, [items, onChange]);

  const handleCellChange = useCallback((index: number, field: string, value: any) => {
    const newItems = [...items];
    const nextItem = { ...newItems[index], [field]: value };
    newItems[index] = withCalculatedLineTotal(nextItem, field);
    onChange(newItems);
  }, [items, onChange]);

  const hasErpIntegration = Boolean(erpConnectionId);

  const fieldNames = selectedLineItemFields ?? LINE_ITEM_FIELDS;
  const showQBItemCell = (SHOW_QB_ITEM_CELL[documentType] ?? false) && hasErpIntegration;
  const allRowsSelected = items.length > 0 && selectedRowIndexes.size === items.length;
  const lineAccountConfig = dynamicErpFieldConfigs.find(
    (config) => config.enabled && config.field.value_scope === 'line' && fieldKeyTail(config.field.field_key) === 'line_account',
  );
  const dynamicLineConfigs = dynamicErpFieldConfigs.filter(
    (config) => config.enabled &&
      config.field.value_scope === 'line' &&
      fieldKeyTail(config.field.field_key) !== 'line_item' &&
      fieldKeyTail(config.field.field_key) !== 'line_account',
  );
  const bulkLineFieldOptions = useMemo(() => {
    const options: Array<{ key: BulkLineFieldKey; label: string; config?: TenantErpFieldConfig }> = [];
    if (showQBItemCell) {
      options.push({ key: 'item', label: 'Item' });
    }
    if (lineAccountConfig) {
      options.push({ key: 'category', label: 'Category / Account' });
    }
    dynamicLineConfigs.forEach((config) => {
      options.push({
        key: config.field.field_key,
        label: config.field.label,
        config,
      });
    });
    return options;
  }, [dynamicLineConfigs, lineAccountConfig, showQBItemCell]);
  const selectedBulkLineField = bulkLineFieldOptions.find((option) => option.key === bulkLineFieldKey) || bulkLineFieldOptions[0];
  const dynamicValuesByKey = useMemo(() => {
    const lookup = new Map<string, ErpFieldValue>();
    dynamicErpValues.forEach((value) => addDynamicValueLookup(lookup, value));
    return lookup;
  }, [dynamicErpValues]);

  const removeLineAccountValue = useCallback((item: LineItem) => {
    if (!onDynamicErpValuesChange) {
      return;
    }
    const lineId = item.id || item.ap_line_id || null;
    onDynamicErpValuesChange(dynamicErpValues.filter((value) => (
      fieldKeyTail(value.field_key) !== 'line_account' ||
      !lineValueKeys(value.field_key, lineId, item.line_number).some((key) => erpValueLineKeys(value).includes(key))
    )));
  }, [dynamicErpValues, onDynamicErpValuesChange]);

  const handleItemLink = useCallback((index: number, itemId: string, itemName: string) => {
    const newItems = [...items];
    const currentItem = newItems[index];
    newItems[index] = {
      ...currentItem,
      erp_item_id: itemId,
      erp_item_name: itemName,
      erp_account_id: '',
      erp_account_name: '',
    };
    onChange(newItems);
    removeLineAccountValue(currentItem);
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      next.set(rowIdentity(currentItem, index), 'item');
      return next;
    });
  }, [items, onChange, removeLineAccountValue]);

  const handleItemUnlink = useCallback((index: number) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], erp_item_id: '', erp_item_name: '' };
    onChange(newItems);
  }, [items, onChange]);

  useEffect(() => {
    setSelectedRowIndexes((prev) => {
      const next = new Set<number>();
      prev.forEach((index) => {
        if (index >= 0 && index < items.length) {
          next.add(index);
        }
      });
      if (next.size === prev.size) {
        return prev;
      }
      return next;
    });
  }, [items.length]);

  useEffect(() => {
    if (!selectedBulkLineField && bulkLineFieldOptions[0]) {
      setBulkLineFieldKey(bulkLineFieldOptions[0].key);
    }
  }, [bulkLineFieldOptions, selectedBulkLineField]);

  const isNumericField = (field: string) =>
    ['qty', 'quantity', 'price', 'unit_price', 'total'].includes(field);

  const isReadOnlyField = (field: string) =>
    ['line_number', 'match_status'].includes(field);

  const isDescriptionField = (field: string) => field === 'description';

  const getColumnWidthClass = (field: string) => {
    if (isDescriptionField(field)) return 'w-auto';
    if (field === 'line_number') return 'w-16';
    if (field === 'uom') return 'w-20';
    if (field === 'po_number') return 'w-32';
    if (field === 'sku') return 'w-28';
    if (['quantity', 'qty'].includes(field)) return 'w-24';
    if (['unit_price', 'price', 'total'].includes(field)) return 'w-28';
    return 'w-28';
  };

  const getColumnMinWidth = (field: string) => {
    if (isDescriptionField(field)) return 320;
    if (field === 'line_number') return 64;
    if (field === 'uom') return 80;
    if (field === 'po_number') return 128;
    if (field === 'sku') return 112;
    if (['quantity', 'qty'].includes(field)) return 96;
    if (['unit_price', 'price', 'total'].includes(field)) return 112;
    return 112;
  };

  const staticColumnsWidth =
    (showQBItemCell ? 48 + 256 : 0) +
    48;
  const selectedFieldsWidth = fieldNames.reduce(
    (total, field) => total + getColumnMinWidth(field),
    0,
  );
  const dynamicFieldsWidth = dynamicLineConfigs.length * 176;
  const tableMinWidth = staticColumnsWidth + selectedFieldsWidth + dynamicFieldsWidth;

  const expandedDescription =
    expandedDescriptionIndex !== null ? String(items[expandedDescriptionIndex]?.description ?? '') : '';

  const handleToggleRowSelection = useCallback((index: number) => {
    setSelectedRowIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAllRows = useCallback(() => {
    setSelectedRowIndexes((prev) => {
      if (prev.size === items.length) {
        return new Set();
      }
      return new Set(items.map((_, index) => index));
    });
  }, [items]);

  const handleBulkItemLink = useCallback((itemId: string, itemName: string) => {
    if (selectedRowIndexes.size === 0) {
      return;
    }
    const nextItems = items.map((item, index) =>
      selectedRowIndexes.has(index)
        ? {
            ...item,
            erp_item_id: itemId,
            erp_item_name: itemName,
            erp_account_id: '',
            erp_account_name: '',
          }
        : item,
    );
    onChange(nextItems);
    if (onDynamicErpValuesChange) {
      const selectedLineAccountKeys = new Set(
        items
          .map((item, index) => selectedRowIndexes.has(index)
            ? lineValueKeys('document_review.line_account', item.id || item.ap_line_id || null, item.line_number)
            : null)
          .flat()
          .filter((key): key is string => Boolean(key)),
      );
      onDynamicErpValuesChange(dynamicErpValues.filter((value) => (
        fieldKeyTail(value.field_key) !== 'line_account' ||
        !erpValueLineKeys(value).some((key) => selectedLineAccountKeys.has(key))
      )));
    }
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      items.forEach((item, index) => {
        if (selectedRowIndexes.has(index)) {
          next.set(rowIdentity(item, index), 'item');
        }
      });
      return next;
    });
  }, [dynamicErpValues, items, onChange, onDynamicErpValuesChange, selectedRowIndexes]);

  const handleBulkItemUnlink = useCallback(() => {
    if (selectedRowIndexes.size === 0) {
      return;
    }
    const nextItems = items.map((item, index) =>
      selectedRowIndexes.has(index)
        ? {
            ...item,
            erp_item_id: '',
            erp_item_name: '',
          }
        : item,
    );
    onChange(nextItems);
  }, [items, onChange, selectedRowIndexes]);

  const selectedLineValueKeysForField = useCallback((fieldKey: string) => {
    return new Set(
      items
        .map((item, index) => selectedRowIndexes.has(index)
          ? lineValueKeys(fieldKey, item.id || item.ap_line_id || null, item.line_number)
          : null)
        .flat()
        .filter((key): key is string => Boolean(key)),
    );
  }, [items, selectedRowIndexes]);

  const removeBulkDynamicLineValues = useCallback((fieldKey: string) => {
    if (!onDynamicErpValuesChange || selectedRowIndexes.size === 0) {
      return;
    }
    const selectedKeys = selectedLineValueKeysForField(fieldKey);
    onDynamicErpValuesChange(dynamicErpValues.filter((value) => (
      value.field_key !== fieldKey ||
      !erpValueLineKeys(value).some((key) => selectedKeys.has(key))
    )));
  }, [dynamicErpValues, onDynamicErpValuesChange, selectedLineValueKeysForField, selectedRowIndexes.size]);

  const handleBulkLineAccountSelect = useCallback((accountId: string, accountName: string) => {
    if (!lineAccountConfig || selectedRowIndexes.size === 0 || !onDynamicErpValuesChange) {
      return;
    }
    const selectedKeys = selectedLineValueKeysForField(lineAccountConfig.field.field_key);
    const remaining = dynamicErpValues.filter((value) => (
      value.field_key !== lineAccountConfig.field.field_key ||
      !erpValueLineKeys(value).some((key) => selectedKeys.has(key))
    ));
    const nextItems = items.map((item, index) =>
      selectedRowIndexes.has(index)
        ? {
            ...item,
            erp_item_id: '',
            erp_item_name: '',
            erp_account_id: accountId,
            erp_account_name: accountName,
          }
        : item,
    );
    const nextValues = items
      .filter((_, index) => selectedRowIndexes.has(index))
      .map((item) => ({
        field_definition_id: lineAccountConfig.field_definition_id,
        field_key: lineAccountConfig.field.field_key,
        line_id: item.id || item.ap_line_id || null,
        value: accountId,
        display_value: accountName,
        reference_option_id: null,
        source: 'manual',
        evidence: { line_number: item.line_number },
      }));
    onChange(nextItems);
    onDynamicErpValuesChange([...remaining, ...nextValues]);
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      items.forEach((item, index) => {
        if (selectedRowIndexes.has(index)) {
          next.set(rowIdentity(item, index), 'category');
        }
      });
      return next;
    });
  }, [
    dynamicErpValues,
    items,
    lineAccountConfig,
    onChange,
    onDynamicErpValuesChange,
    selectedLineValueKeysForField,
    selectedRowIndexes,
  ]);

  const handleBulkDynamicApply = useCallback((config: TenantErpFieldConfig, template: DynamicErpBulkValueTemplate) => {
    if (!onDynamicErpValuesChange || selectedRowIndexes.size === 0) {
      return;
    }
    const selectedKeys = selectedLineValueKeysForField(config.field.field_key);
    const remaining = dynamicErpValues.filter((value) => (
      value.field_key !== config.field.field_key ||
      !erpValueLineKeys(value).some((key) => selectedKeys.has(key))
    ));
    const nextValues = items
      .filter((_, index) => selectedRowIndexes.has(index))
      .map((item) => ({
        field_definition_id: config.field_definition_id,
        field_key: config.field.field_key,
        line_id: item.id || item.ap_line_id || null,
        value: template.value,
        display_value: template.display_value,
        reference_option_id: template.reference_option_id,
        source: 'manual',
        evidence: { line_number: item.line_number },
      }));
    onDynamicErpValuesChange([...remaining, ...nextValues]);
  }, [dynamicErpValues, items, onDynamicErpValuesChange, selectedLineValueKeysForField, selectedRowIndexes]);

  const handleBulkFieldClear = useCallback(() => {
    if (!selectedBulkLineField || selectedRowIndexes.size === 0) {
      return;
    }
    if (selectedBulkLineField.key === 'item') {
      handleBulkItemUnlink();
      return;
    }
    if (selectedBulkLineField.key === 'category' && lineAccountConfig) {
      removeBulkDynamicLineValues(lineAccountConfig.field.field_key);
      onChange(items.map((item, index) => selectedRowIndexes.has(index)
        ? { ...item, erp_account_id: '', erp_account_name: '' }
        : item));
      return;
    }
    if (selectedBulkLineField.config) {
      removeBulkDynamicLineValues(selectedBulkLineField.config.field.field_key);
    }
  }, [
    handleBulkItemUnlink,
    lineAccountConfig,
    removeBulkDynamicLineValues,
    selectedBulkLineField,
    selectedRowIndexes.size,
  ]);

  const handleDynamicValueChange = useCallback((nextValue: ErpFieldValue, item: LineItem) => {
    if (!onDynamicErpValuesChange) {
      return;
    }
    const nextKeys = lineValueKeys(nextValue.field_key, nextValue.line_id, item.line_number);
    const remaining = dynamicErpValues.filter((value) => (
      !erpValueLineKeys(value).some((key) => nextKeys.includes(key))
    ));
    onDynamicErpValuesChange([...remaining, nextValue]);
  }, [dynamicErpValues, onDynamicErpValuesChange]);

  const getLineAccountValue = useCallback((item: LineItem) => {
    if (!lineAccountConfig) {
      return undefined;
    }
    return getDynamicLineValue(
      dynamicValuesByKey,
      lineAccountConfig.field.field_key,
      item.id || item.ap_line_id || null,
      item.line_number,
    );
  }, [dynamicValuesByKey, lineAccountConfig]);

  const getPostingLineType = useCallback((item: LineItem, index: number): PostingLineType => {
    return linePostingTypes.get(rowIdentity(item, index)) ||
      ((getLineAccountValue(item) || item.erp_account_id) && !item.erp_item_id ? 'category' : 'item');
  }, [getLineAccountValue, linePostingTypes]);

  const handlePostingLineTypeChange = useCallback((index: number, nextType: PostingLineType) => {
    const item = items[index];
    if (!item) {
      return;
    }
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      next.set(rowIdentity(item, index), nextType);
      return next;
    });
    if (nextType === 'item') {
      removeLineAccountValue(item);
      const newItems = [...items];
      newItems[index] = { ...item, erp_account_id: '', erp_account_name: '' };
      onChange(newItems);
      return;
    }
    const newItems = [...items];
    newItems[index] = { ...item, erp_item_id: '', erp_item_name: '' };
    onChange(newItems);
  }, [items, onChange, removeLineAccountValue]);

  const handleLineAccountChange = useCallback((nextValue: ErpFieldValue, item: LineItem, index: number) => {
    const newItems = [...items];
    newItems[index] = {
      ...item,
      erp_item_id: '',
      erp_item_name: '',
      erp_account_id: nextValue.value,
      erp_account_name: nextValue.display_value,
    };
    onChange(newItems);
    setLinePostingTypes((prev) => {
      const next = new Map(prev);
      next.set(rowIdentity(item, index), 'category');
      return next;
    });
    handleDynamicValueChange(nextValue, item);
  }, [handleDynamicValueChange, items, onChange]);

  const handleLineAccountSelect = useCallback((accountId: string, accountName: string, item: LineItem, index: number) => {
    if (!lineAccountConfig) {
      return;
    }
    handleLineAccountChange({
      field_definition_id: lineAccountConfig.field_definition_id,
      field_key: lineAccountConfig.field.field_key,
      line_id: item.id || item.ap_line_id || null,
      value: accountId,
      display_value: accountName,
      reference_option_id: null,
      source: 'manual',
      evidence: { line_number: item.line_number },
    }, item, index);
  }, [handleLineAccountChange, lineAccountConfig]);

  const handleLineAccountClear = useCallback((item: LineItem, index: number) => {
    removeLineAccountValue(item);
    const newItems = [...items];
    newItems[index] = { ...item, erp_account_id: '', erp_account_name: '' };
    onChange(newItems);
  }, [items, onChange, removeLineAccountValue]);

  return (
    <div className={`${fillAvailableHeight ? 'flex min-h-0 flex-col gap-3' : 'space-y-3'} ${className}`}>
      {items.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400">
          <p className="text-sm">No line items</p>
        </div>
      ) : (
        <div className={fillAvailableHeight ? 'flex min-h-0 flex-1 flex-col gap-3' : 'space-y-3'}>
          {bulkLineFieldOptions.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900">
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                    {selectedRowIndexes.size} selected
                  </span>
                  <button
                    type="button"
                    onClick={handleToggleSelectAllRows}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-200"
                  >
                    {allRowsSelected ? 'Clear all' : 'Select all'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedRowIndexes(new Set())}
                    className="text-xs font-medium text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
                  >
                    Clear selection
                  </button>
                </div>
                <div className="h-5 w-px bg-gray-300 dark:bg-gray-700" />
                <SearchableSelect
                  value={selectedBulkLineField?.key || ''}
                  onChange={setBulkLineFieldKey}
                  options={bulkLineFieldOptions.map((option) => ({ id: option.key, label: option.label }))}
                  placeholder="Bulk field"
                  clearable={false}
                  className="w-48"
                />
                <div className="min-w-[240px] flex-1 sm:flex-none">
                  {selectedBulkLineField?.key === 'item' ? (
                    <QBItemCell
                      erpConnectionId={erpConnectionId}
                      value={null}
                      onSelect={handleBulkItemLink}
                      onClear={() => undefined}
                      disabled={selectedRowIndexes.size === 0}
                    />
                  ) : selectedBulkLineField?.key === 'category' ? (
                    <QBAccountCell
                      erpConnectionId={erpConnectionId}
                      billDetailProvider={billDetailProvider}
                      value={null}
                      onSelect={handleBulkLineAccountSelect}
                      onClear={() => undefined}
                      disabled={selectedRowIndexes.size === 0}
                    />
                  ) : selectedBulkLineField?.config ? (
                    <DynamicErpBulkFieldPicker
                      config={selectedBulkLineField.config}
                      disabled={selectedRowIndexes.size === 0}
                      onApply={(template) => handleBulkDynamicApply(selectedBulkLineField.config!, template)}
                    />
                  ) : null}
                </div>
                <button
                  type="button"
                  onClick={handleBulkFieldClear}
                  disabled={selectedRowIndexes.size === 0 || !selectedBulkLineField}
                  className="rounded border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:border-gray-400 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:border-gray-500 dark:hover:text-gray-100"
                >
                  Clear field
                </button>
              </div>
            </div>
          )}
          <div className={`${fillAvailableHeight ? 'min-h-0 flex-1 overflow-y-auto' : ''} overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700`}>
            <table className="w-full table-fixed text-sm" style={{ minWidth: tableMinWidth }}>
            <thead className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
              <tr>
                {showQBItemCell && (
                  <th className="w-12 px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      checked={allRowsSelected}
                      onChange={handleToggleSelectAllRows}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      aria-label="Select all line items"
                    />
                  </th>
                )}
                {fieldNames.map((field) => (
                  <th
                    key={field}
                    className={`px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 capitalize whitespace-nowrap ${getColumnWidthClass(field)}`}
                  >
                    {field.replace(/_/g, ' ')}
                  </th>
                ))}
                {dynamicLineConfigs.map((config) => (
                  <th
                    key={config.id}
                    className="w-44 px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap"
                  >
                    {config.field.label}
                  </th>
                ))}
                {showQBItemCell && (
                  <th className="w-64 px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    Line Type / Selection
                  </th>
                )}
                <th className="w-12 px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-300">
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => {
                const linePostingType = getPostingLineType(item, index);
                const lineAccountValue = getLineAccountValue(item);
                return (
                <tr
                  key={index}
                  className="border-b border-gray-200 dark:border-gray-700"
                >
                  {showQBItemCell && (
                    <td className="px-3 py-1.5 align-middle">
                      <input
                        type="checkbox"
                        checked={selectedRowIndexes.has(index)}
                        onChange={() => handleToggleRowSelection(index)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        aria-label={`Select line item ${index + 1}`}
                      />
                    </td>
                  )}
                  {fieldNames.map((field) => (
                    <td
                      key={`${index}-${field}`}
                      className={`px-2 py-1.5 ${isDescriptionField(field) ? 'min-w-0' : 'whitespace-nowrap'}`}
                    >
                      {isReadOnlyField(field) ? (
                        <span className="px-1 text-gray-500 dark:text-gray-400">
                          {String(item[field] ?? '-')}
                        </span>
                      ) : isDescriptionField(field) ? (
                        <div className="flex w-full min-w-0 items-center gap-2">
                          <div className="min-w-0 flex-1 rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                            <span className="block truncate">
                              {String(item[field] || 'No description')}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setExpandedDescriptionIndex(index)}
                            className="rounded p-1 text-blue-600 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950"
                            title="View and edit description"
                            aria-label={`View and edit description for line item ${index + 1}`}
                          >
                            <Info className="h-4 w-4" />
                          </button>
                        </div>
                      ) : isNumericField(field) ? (
                        <ExpressionNumberInput
                          step="0.01"
                          value={item[field] ?? ''}
                          onValueChange={(value) => handleCellChange(index, field, value ?? '')}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      ) : (
                        <input
                          type="text"
                          value={item[field] ?? ''}
                          onChange={(e) => handleCellChange(index, field, e.target.value)}
                          className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      )}
                    </td>
                  ))}
                  {dynamicLineConfigs.map((config) => {
                    const currentValue = getDynamicLineValue(
                      dynamicValuesByKey,
                      config.field.field_key,
                      item.id || item.ap_line_id || null,
                      item.line_number,
                    );
                    return (
                      <td key={`${index}-${config.id}`} className="px-2 py-1.5">
                        <DynamicErpLineFieldCell
                          config={config}
                          item={item}
                          value={currentValue}
                          onChange={(value) => handleDynamicValueChange(value, item)}
                        />
                      </td>
                    );
                  })}
                  {showQBItemCell && (
                    <td className="px-2 py-1.5">
                      <div className="space-y-2">
                        {lineAccountConfig && (
                          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5 text-xs font-medium dark:border-gray-600 dark:bg-gray-800">
                            <button
                              type="button"
                              onClick={() => handlePostingLineTypeChange(index, 'item')}
                              className={`rounded px-2 py-1 transition-colors ${
                                linePostingType === 'item'
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                              }`}
                            >
                              Item
                            </button>
                            <button
                              type="button"
                              onClick={() => handlePostingLineTypeChange(index, 'category')}
                              className={`rounded px-2 py-1 transition-colors ${
                                linePostingType === 'category'
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                              }`}
                            >
                              Category
                            </button>
                          </div>
                        )}
                        {linePostingType === 'category' && lineAccountConfig ? (
                          <QBAccountCell
                            erpConnectionId={erpConnectionId}
                            billDetailProvider={billDetailProvider}
                            value={String(lineAccountValue?.value || item.erp_account_id || '') || null}
                            displayValue={lineAccountValue?.display_value || item.erp_account_name || null}
                            onSelect={(accountId, accountName) => handleLineAccountSelect(accountId, accountName, item, index)}
                            onClear={() => handleLineAccountClear(item, index)}
                          />
                        ) : (
                          <QBItemCell
                            erpConnectionId={erpConnectionId}
                            value={item.erp_item_id || null}
                            onSelect={(itemId, itemName) => handleItemLink(index, itemId, itemName)}
                            onClear={() => handleItemUnlink(index)}
                          />
                        )}
                      </div>
                    </td>
                  )}
                  <td className="px-2 py-1.5 text-right">
                    <button
                      onClick={() => handleDeleteItem(index)}
                      className="p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-600 dark:text-red-400"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={handleAddItem}
        className="w-full flex items-center justify-center gap-2 px-3 py-2 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:border-gray-400 dark:hover:border-gray-500 transition"
      >
        <Plus className="h-4 w-4" />
        Add Line Item
      </button>

      <Dialog
        open={expandedDescriptionIndex !== null}
        onOpenChange={(open) => {
          if (!open) {
            setExpandedDescriptionIndex(null);
          }
        }}
        className="max-w-2xl"
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl text-gray-900">Line Item Description</DialogTitle>
            <DialogDescription className="text-gray-500">
              Review and edit the full description without compressing the table layout.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <textarea
              value={expandedDescription}
              onChange={(e) => {
                if (expandedDescriptionIndex !== null) {
                  handleCellChange(expandedDescriptionIndex, 'description', e.target.value);
                }
              }}
              rows={8}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              placeholder="Enter line item description"
              autoFocus
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setExpandedDescriptionIndex(null)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:border-gray-400 hover:text-gray-900 dark:border-gray-600 dark:text-gray-200 dark:hover:border-gray-500 dark:hover:text-gray-100"
              >
                Done
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LineItemsEditor;
